import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';

// Column type rendering
export type ColumnType =
  | 'default'
  | 'chip'
  | 'patient'
  | 'action'
  | 'mono'
  | 'actionButton'
  | 'issues';

export interface ColumnDef {
  key: string;
  label: string;
  width: string; // CSS grid width e.g. '80px', '1.2fr', '2fr'
  type?: ColumnType;
  align?: 'left' | 'right' | 'center';
  required?: boolean;
  /** Enables the optional table-header sorting interaction for this column. */
  sortable?: boolean;
  /** Direction to use when this column becomes the active sort. */
  defaultSortDirection?: 'asc' | 'desc';
}

export type DataTableSort = {
  columnKey: string;
  direction: 'asc' | 'desc';
  onChange: (columnKey: string, direction: 'asc' | 'desc') => void;
};

// Row data is inherently dynamic — columns are user-configurable
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RowData = Record<string, any>;

interface DataTableProps {
  columns: ColumnDef[];
  rows: RowData[];
  onRowClick?: (row: RowData) => void;
  /** Fires once per pointer entry into a row — the hover-prefetch hook
   *  (GDN-3201). Guard rails (once-per-row, TTL, touch) live in the caller;
   *  the table only reports the gesture. */
  onRowHover?: (row: RowData) => void;
  onAction?: (row: RowData) => void;
  idKey?: string;
  isFetching?: boolean;
  /** Render a custom element at the end of each row (e.g., action menu) */
  renderRowEnd?: (row: RowData) => React.ReactNode;
  /** Optional label and width for a multi-control row action area. */
  rowEndLabel?: string;
  rowEndWidth?: string;
  /** Enable row selection with checkboxes */
  selectedIds?: Set<string>;
  onSelectionChange?: (ids: Set<string>) => void;
  /** Optional row-level selection gate. Defaults to all rows selectable. */
  isRowSelectable?: (row: RowData) => boolean;
  /** Accessible label for the header selection control. */
  selectionLabel?: string;
  /** Accessible label for an individual row selection control. */
  getRowSelectionLabel?: (row: RowData) => string;
  /** Override cell rendering for specific columns. Return null to fall back to default. */
  renderCell?: (column: ColumnDef, row: RowData) => React.ReactNode | null;
  /** Compute custom sx overrides per row (e.g., opacity for dimmed rows) */
  rowSx?: (row: RowData) => Record<string, unknown>;
  /**
   * Group header rows (GDN-3383): the text to render in a band ABOVE this
   * row, or null for a row that continues the group above it. The CALLER
   * owns grouping — which rows belong together, their order, and what the
   * band says; the table owns only the band's presentation, so every
   * grouped list in the product reads the same. Rows arrive already ordered
   * by group. Omitted, nothing changes: an ungrouped table renders exactly
   * as before.
   */
  groupHeaderForRow?: (row: RowData, index: number) => string | null;
  /** Optional controlled sort state. Existing tables remain unchanged when omitted. */
  sort?: DataTableSort;
  /**
   * Contextual header (GDN-3216): when set, this node replaces the column
   * header row — the Polaris/Carbon bulk-actions pattern. The overlay owns
   * its full styling; the table provides only the slot (and stickiness).
   */
  headerOverlay?: React.ReactNode;
  /**
   * Sticks the header row (or its overlay) at this `top` against the nearest
   * scrolling ancestor — in the app, the window, offset below the page
   * header via the measured jotBillStickyTop. The root's `overflow: clip`
   * (not `hidden`) is what lets stickiness reach the window.
   */
  stickyHeaderTop?: string;
  /** Tooltip shown on DISABLED row checkboxes, so a lock is visibly a lock
   *  (e.g. "A submission batch is finishing"). */
  rowSelectionDisabledReason?: string;
  /**
   * Drop the table's own card chrome (border and rounded corners) because a
   * card already owns it — the JotBill Home queue cards, where the table IS
   * the card's body and a second border would draw a box inside a box. The
   * grey header row still separates the two. Default false: every existing
   * table is its own card and keeps its chrome.
   */
  embedded?: boolean;
}

const chipColorMap: Record<string, { bg: string; text: string; border: string }> = {
  drafts: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  ready: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  inFlight: { bg: '#F5F4FB', text: '#36229E', border: '#E6E1FF' },
  paid: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  review: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  clean: { bg: '#F0FDF4', text: '#166534', border: '#BBF7D0' },
  denials: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
  reversals: { bg: '#FFFBEB', text: '#92400E', border: '#FDE68A' },
  imported: { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' },
  open: { bg: '#F5F4FB', text: '#36229E', border: '#E6E1FF' },
  void: { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' },
  uncollectible: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA' },
};

const severityColors: Record<string, string> = {
  error: '#DC2626',
  warning: '#D97706',
  success: '#16A34A',
  info: '#36229E',
};

export function CellContent({
  column,
  value,
  row,
  onAction,
}: {
  column: ColumnDef;
  value: unknown;
  row: RowData;
  onAction?: (row: RowData) => void;
}) {
  // Safely stringify — guard against objects
  const strValue = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  switch (column.type) {
    case 'chip': {
      const colors = chipColorMap[strValue] ?? chipColorMap['imported'];
      const label = String(
        row[`${column.key}_label`] ?? row['phase_label'] ?? row['outcome_label'] ?? strValue,
      );
      return (
        <Chip
          label={label}
          size="small"
          sx={{
            bgcolor: colors.bg,
            color: colors.text,
            border: `1px solid ${colors.border}`,
            fontWeight: 500,
            fontSize: '0.6875rem',
            height: 22,
          }}
        />
      );
    }
    case 'patient': {
      const subtitle = row[`${column.key}_subtitle`] as string | undefined;
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            {strValue}
          </Typography>
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.6875rem' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      );
    }
    case 'action': {
      const severity = String(
        row[`${column.key}_severity`] ??
          (row['what_to_do'] as Record<string, unknown> | undefined)?.severity ??
          '',
      );
      const color = severity ? severityColors[severity] : undefined;
      const rawText =
        typeof value === 'object' && value != null
          ? (value as Record<string, unknown>).text
          : value;
      const displayText = typeof rawText === 'string' ? rawText : strValue;
      return (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            color: color ?? 'text.primary',
            fontWeight: severity === 'error' ? 500 : 400,
          }}
        >
          {displayText}
        </Typography>
      );
    }
    case 'mono':
      return (
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {strValue}
        </Typography>
      );
    case 'actionButton':
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={e => {
            e.stopPropagation();
            onAction?.(row);
          }}
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '6px',
            py: 0.25,
            px: 1.5,
          }}
        >
          {strValue}
        </Button>
      );
    case 'issues': {
      const errors = (row['error_count'] as number) ?? 0;
      const warnings = (row['warning_count'] as number) ?? 0;
      if (!errors && !warnings) return null;
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {errors > 0 && (
            <Box
              sx={{
                bgcolor: '#FEF2F2',
                color: '#DC2626',
                px: 0.75,
                py: 0.125,
                borderRadius: '4px',
                fontSize: '0.625rem',
                fontWeight: 600,
              }}
            >
              {errors}e
            </Box>
          )}
          {warnings > 0 && (
            <Box
              sx={{
                bgcolor: '#FFFBEB',
                color: '#D97706',
                px: 0.75,
                py: 0.125,
                borderRadius: '4px',
                fontSize: '0.625rem',
                fontWeight: 600,
              }}
            >
              {warnings}w
            </Box>
          )}
        </Box>
      );
    }
    default:
      return (
        <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.primary' }}>
          {strValue || '—'}
        </Typography>
      );
  }
}

export const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  onRowClick,
  onRowHover,
  onAction,
  idKey = 'id',
  isFetching = false,
  renderRowEnd,
  rowEndLabel,
  rowEndWidth = '40px',
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  selectionLabel,
  getRowSelectionLabel,
  renderCell,
  rowSx,
  sort,
  groupHeaderForRow,
  headerOverlay,
  stickyHeaderTop,
  rowSelectionDisabledReason,
  embedded = false,
}) => {
  const hasRowEnd = Boolean(renderRowEnd);
  const selectable = Boolean(selectedIds && onSelectionChange);
  const gridTemplate =
    (selectable ? '36px ' : '') +
    columns.map(c => c.width).join(' ') +
    (hasRowEnd ? ` ${rowEndWidth}` : '');

  const selectableRows = selectable ? rows.filter(r => isRowSelectable?.(r) ?? true) : [];
  const allSelected =
    selectable &&
    selectableRows.length > 0 &&
    selectableRows.every(r => selectedIds?.has(r[idKey] as string) ?? false);
  const someSelected =
    selectable && selectableRows.some(r => selectedIds?.has(r[idKey] as string) ?? false);

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(selectableRows.map(r => r[idKey] as string)));
    }
  };

  const handleToggle = (row: RowData) => {
    if (!onSelectionChange || !selectedIds) return;
    if (isRowSelectable && !isRowSelectable(row)) return;
    const rowId = row[idKey] as string;
    const next = new Set(selectedIds);
    if (next.has(rowId)) next.delete(rowId);
    else next.add(rowId);
    onSelectionChange(next);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: '#fff',
        borderRadius: embedded ? 0 : '10px',
        ...(embedded ? {} : { border: '1px solid', borderColor: 'divider' }),
        /* clip, not hidden: same corner clipping, but clip creates no scroll
           context — so the sticky header sticks to the WINDOW scroll instead
           of silently no-opping inside this box (GDN-3216). */
        overflow: 'clip',
      }}
    >
      {/* Fetching overlay */}
      {isFetching && rows.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(255,255,255,0.6)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {/* Header — or the contextual overlay standing in for it (GDN-3216).
          Both stick when the caller passes an offset; zIndex above the
          fetching overlay so the bar stays readable while rows refetch. */}
      {headerOverlay != null && (
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            ...(stickyHeaderTop != null
              ? { position: 'sticky', top: stickyHeaderTop, zIndex: 2 }
              : {}),
          }}
        >
          {headerOverlay}
        </Box>
      )}
      <Box
        sx={{
          /* The column header stays mounted under an overlay (display:none)
             so its select-all state and measurements survive the swap. */
          display: headerOverlay != null ? 'none' : 'grid',
          gridTemplateColumns: gridTemplate,
          gap: 1.5,
          px: 2.5,
          py: 1.25,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          ...(stickyHeaderTop != null
            ? { position: 'sticky', top: stickyHeaderTop, zIndex: 2 }
            : {}),
        }}
      >
        {selectable && (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={handleSelectAll}
            slotProps={{ input: { 'aria-label': selectionLabel ?? 'Select all rows' } }}
            /* Same halo/hit-area treatment as the row checkbox below. */
            sx={{ justifySelf: 'center', m: -1, p: 1 }}
          />
        )}
        {columns.map(col => {
          const active = sort?.columnKey === col.key;
          const direction = active ? sort.direction : (col.defaultSortDirection ?? 'asc');
          const headerTextSx = {
            color: 'text.secondary',
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          } as const;

          return (
            <Box
              key={col.key}
              aria-sort={
                col.sortable && sort
                  ? active
                    ? direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                  : undefined
              }
              role="columnheader"
              sx={{ minWidth: 0, textAlign: col.align ?? 'left' }}
            >
              {col.sortable && sort ? (
                <TableSortLabel
                  active={active}
                  direction={direction}
                  onClick={() =>
                    sort.onChange(
                      col.key,
                      active ? (sort.direction === 'asc' ? 'desc' : 'asc') : direction,
                    )
                  }
                  sx={{
                    ...headerTextSx,
                    '& .MuiTableSortLabel-icon': { fontSize: '1rem' },
                  }}
                >
                  {col.label}
                </TableSortLabel>
              ) : (
                <Typography variant="subtitle2" sx={headerTextSx}>
                  {col.label}
                </Typography>
              )}
            </Box>
          );
        })}
        {hasRowEnd && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textAlign: 'right',
              textTransform: 'uppercase',
            }}
            variant="subtitle2"
          >
            {rowEndLabel}
          </Typography>
        )}
      </Box>

      {/* Rows */}
      {rows.map((row, idx) => {
        const groupHeader = groupHeaderForRow?.(row, idx) ?? null;
        return (
          <React.Fragment key={(row[idKey] as string) ?? idx}>
            {groupHeader != null && (
              <Box
                data-testid="data-table-group-header"
                /* No ARIA role. This table is a div grid with no `table` or
                   `row` ancestor, so `rowheader` would be an orphan role,
                   and an `aria-label` on a div that already has visible text
                   would OVERRIDE that text for a screen reader rather than
                   describe it. Plain static text is what this is, and it is
                   what assistive tech reads. */
                sx={{
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  color: 'text.primary',
                  fontSize: '0.6875rem',
                  /* Heavier than the column header above it (600), because
                     this band names the rows under it rather than the
                     columns across them. */
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  px: 2.5,
                  py: 0.75,
                  textTransform: 'uppercase',
                }}
              >
                {groupHeader}
              </Box>
            )}
            <Box
              data-row-id={(row[idKey] as string) ?? undefined}
              onClick={() => onRowClick?.(row)}
              onMouseEnter={onRowHover ? () => onRowHover(row) : undefined}
              sx={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                gap: 1.5,
                alignItems: 'center',
                px: 2.5,
                py: 1.25,
                minHeight: 48,
                borderBottom: idx < rows.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                cursor: onRowClick ? 'pointer' : 'default',
                '&:hover': onRowClick ? { bgcolor: 'rgba(54, 34, 158, 0.02)' } : undefined,
                ...(selectedIds?.has(row[idKey] as string)
                  ? {
                      bgcolor: 'action.selected',
                      '&:hover': { bgcolor: 'action.selected' },
                    }
                  : {}),
                ...rowSx?.(row),
              }}
            >
              {selectable &&
                (() => {
                  const rowDisabled = isRowSelectable ? !isRowSelectable(row) : false;
                  const checkbox = (
                    <Checkbox
                      size="small"
                      checked={selectedIds?.has(row[idKey] as string) ?? false}
                      disabled={rowDisabled}
                      onChange={e => {
                        e.stopPropagation();
                        handleToggle(row);
                      }}
                      onClick={e => e.stopPropagation()}
                      slotProps={{
                        input: {
                          'aria-label': getRowSelectionLabel?.(row) ?? 'Select row',
                        },
                      }}
                      /* Two things make this halo circular (GDN-3214). The padding
                     IS the circular hover/hit area — p:0 had squished it to the
                     glyph — and the checkbox is a direct grid item, which grids
                     STRETCH horizontally by default: without justifySelf the
                     root is track-wide, and borderRadius:50% on a wide box is
                     an oval no matter the padding. Negative margin cancels the
                     layout footprint so row height stays unchanged. */
                      sx={{ justifySelf: 'center', m: -1, p: 1 }}
                    />
                  );
                  /* A disabled checkbox swallows pointer events, so the tooltip
                 needs a live wrapper — which then becomes the grid item and
                 must carry the centering itself. */
                  return rowDisabled && rowSelectionDisabledReason ? (
                    <Tooltip title={rowSelectionDisabledReason}>
                      <Box component="span" sx={{ display: 'inline-flex', justifySelf: 'center' }}>
                        {checkbox}
                      </Box>
                    </Tooltip>
                  ) : (
                    checkbox
                  );
                })()}
              {columns.map(col => {
                const customCell = renderCell?.(col, row);
                return (
                  <Box
                    key={col.key}
                    sx={{ textAlign: col.align ?? 'left', overflow: 'hidden', minWidth: 0 }}
                  >
                    {customCell !== undefined && customCell !== null ? (
                      customCell
                    ) : (
                      <CellContent
                        column={col}
                        value={row[col.key] as unknown}
                        row={row}
                        onAction={onAction}
                      />
                    )}
                  </Box>
                );
              })}
              {renderRowEnd && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: rowEndLabel || rowEndWidth !== '40px' ? 'flex-end' : 'center',
                  }}
                >
                  {renderRowEnd(row)}
                </Box>
              )}
            </Box>
          </React.Fragment>
        );
      })}

      {rows.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No results
          </Typography>
        </Box>
      )}
    </Box>
  );
};
